import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePromotion } from '@/features/promotions/hooks/use-promotion';
import { useDeletePromotion } from '@/features/promotions/hooks/use-delete-promotion';
import { useUpdatePromotionStatus } from '@/features/promotions/hooks/use-update-promotion-status';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { Skeleton } from '@/components/atoms/skeleton';
import { Separator } from '@/components/atoms/separator';
import { DeleteConfirmDialog } from '@/components/molecules/delete-confirm-dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Edit,
  Tag,
  Percent,
  Truck,
  Gift,
  Calendar,
  Users,
  Store,
  Package,
  Layers,
  CheckCircle,
  XCircle,
  AlertCircle,
  StickyNote,
  Hash,
  Play,
  Pause,
  Ban,
  Loader2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatDate, formatPrice, getImageUrl, convertFromEur } from '@/lib/utils';
import { useConfigStore } from '@/lib/stores/config.store';
import type { PromotionStatus, PromotionType } from '@/features/promotions/types/promotions.types';

const statusVariantMap: Record<PromotionStatus, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  active: 'default',
  paused: 'secondary',
  cancelled: 'destructive',
  scheduled: 'secondary',
  expired: 'destructive',
  draft: 'outline',
};

const typeIconMap: Record<PromotionType, React.ElementType> = {
  percentage: Percent,
  fixed_amount: Tag,
  free_shipping: Truck,
  buy_x_get_y: Gift,
  bundle: Layers,
};

export default function PromotionDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: promotion, isLoading, error, isFetching } = usePromotion(id ?? '');
  const deletePromotion = useDeletePromotion();
  const updateStatus = useUpdatePromotionStatus();
  const { currency } = useConfigStore();

  const handleStatusChange = (newStatus: PromotionStatus) => {
    if (!promotion) return;
    updateStatus.mutate(
      { refId: promotion.refId, status: newStatus },
      {
        onSuccess: () => {
          toast.success(t('promotions.statusUpdateSuccess', 'Statut mis à jour avec succès'));
        },
        onError: () => {
          toast.error(t('promotions.statusUpdateError', 'Erreur lors de la mise à jour du statut'));
        },
      }
    );
  };

  const handleDelete = () => {
    if (!promotion) return;
    deletePromotion.mutate(promotion.refId, {
      onSuccess: () => {
        toast.success(t('promotions.deleteSuccess', 'Promotion supprimée avec succès'));
        navigate('/promotions');
      },
      onError: () => {
        toast.error(t('promotions.deleteError', 'Erreur lors de la suppression de la promotion'));
      },
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-9 w-9" />
          <Skeleton className="h-8 w-48" />
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardContent className="pt-6">
              <Skeleton className="h-32 w-full" />
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <Skeleton className="h-32 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error || !promotion) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('promotions.error')}</p>
      </div>
    );
  }

  const TypeIcon = typeIconMap[promotion.type] || Tag;
  const usagePercentage = promotion.maxUsage
    ? Math.min((promotion.currentUsage / promotion.maxUsage) * 100, 100)
    : 0;

  return (
    <div className="space-y-6 w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-4 min-w-0">
          <Link to="/promotions">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="size-5" />
            </Button>
          </Link>
          <div
            className="rounded-lg p-3 shrink-0"
            style={{ backgroundColor: promotion.badgeColor ? `${promotion.badgeColor}20` : undefined }}
          >
            <TypeIcon
              className="size-6"
              style={{ color: promotion.badgeColor || undefined }}
            />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold truncate">{promotion.name}</h1>
            {promotion.displayName && promotion.displayName !== promotion.name && (
              <p className="text-muted-foreground truncate">
                {t('promotions.displayName')}: {promotion.displayName}
              </p>
            )}
            {promotion.description && (
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{promotion.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            {(updateStatus.isPending || isFetching) && (
              <Loader2 className="size-4 animate-spin text-muted-foreground" />
            )}
            <Badge variant={statusVariantMap[promotion.status]}>
              {t(`promotions.status.${promotion.status}`)}
            </Badge>
          </div>
          {promotion.priority !== undefined && promotion.priority > 0 && (
            <Badge variant="outline" className="gap-1">
              <Hash className="size-3" />
              {promotion.priority}
            </Badge>
          )}
          {/* Status action buttons */}
          {(promotion.status === 'draft' || promotion.status === 'paused' || promotion.status === 'scheduled') && (
            <Button
              variant="default"
              size="sm"
              onClick={() => handleStatusChange('active')}
              disabled={updateStatus.isPending}
            >
              <Play className="size-4 mr-2" />
              {t('promotions.activate', 'Activer')}
            </Button>
          )}
          {promotion.status === 'active' && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleStatusChange('paused')}
              disabled={updateStatus.isPending}
            >
              <Pause className="size-4 mr-2" />
              {t('promotions.pause', 'Mettre en pause')}
            </Button>
          )}
          {(promotion.status === 'active' || promotion.status === 'paused' || promotion.status === 'scheduled') && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => handleStatusChange('cancelled')}
              disabled={updateStatus.isPending}
            >
              <Ban className="size-4 mr-2" />
              {t('promotions.cancel', 'Annuler')}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/promotions/${promotion.refId}/edit`)}
          >
            <Edit className="size-4 mr-2" />
            {t('common.edit')}
          </Button>
          <DeleteConfirmDialog
            itemName={promotion.displayName || promotion.name}
            onConfirm={handleDelete}
            isLoading={deletePromotion.isPending}
            variant="button"
          />
        </div>
      </div>

      {/* Banner */}
      {promotion.bannerUrl && (
        <div className="h-48 rounded-lg overflow-hidden bg-muted">
          <img
            src={getImageUrl(promotion.bannerUrl) || promotion.bannerUrl}
            alt={promotion.name}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* Discount Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TypeIcon className="size-5" />
              {t('promotions.discountInfo')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t('promotions.type')}</span>
              <Badge variant="outline">{t(`promotions.types.${promotion.type}`)}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t('promotions.value')}</span>
              <span className="text-2xl font-bold" style={{ color: promotion.badgeColor || undefined }}>
                {promotion.type === 'percentage'
                  ? `${promotion.value}%`
                  : promotion.type === 'free_shipping'
                  ? t('promotions.freeShipping')
                  : formatPrice(convertFromEur(promotion.value, currency), currency)}
              </span>
            </div>
            {/* Buy X Get Y specific fields */}
            {promotion.type === 'buy_x_get_y' && (
              <>
                {promotion.buyQuantity && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">{t('promotions.buyQuantity')}</span>
                    <span className="font-medium">{promotion.buyQuantity}</span>
                  </div>
                )}
                {promotion.getQuantity && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">{t('promotions.getQuantity')}</span>
                    <span className="font-medium text-green-600">{promotion.getQuantity} {t('promotions.free')}</span>
                  </div>
                )}
              </>
            )}
            <Separator />
            {/* Minimum requirements */}
            {promotion.minPurchaseAmount && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t('promotions.minPurchase')}</span>
                <span>{formatPrice(convertFromEur(promotion.minPurchaseAmount, currency), currency)}</span>
              </div>
            )}
            {promotion.minQuantity && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t('promotions.minQuantity')}</span>
                <span>{promotion.minQuantity} {t('promotions.items')}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Usage Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="size-5" />
              {t('promotions.usageInfo')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t('promotions.totalUsage')}</span>
              <span className="font-medium">
                {promotion.currentUsage}
                {promotion.maxUsage ? ` / ${promotion.maxUsage}` : ` (${t('promotions.unlimited')})`}
              </span>
            </div>
            {promotion.maxUsage && (
              <div className="space-y-1">
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all"
                    style={{ width: `${usagePercentage}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground text-right">{usagePercentage.toFixed(0)}%</p>
              </div>
            )}
            {promotion.maxUsagePerCustomer && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t('promotions.maxPerCustomer')}</span>
                <span>{promotion.maxUsagePerCustomer} {t('promotions.perCustomer')}</span>
              </div>
            )}
            <Separator />
            {/* Stacking info */}
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t('promotions.stackable')}</span>
              <div className="flex items-center gap-1">
                {promotion.stackable ? (
                  <>
                    <CheckCircle className="size-4 text-green-500" />
                    <span className="text-green-600">{t('common.yes')}</span>
                  </>
                ) : (
                  <>
                    <XCircle className="size-4 text-red-500" />
                    <span className="text-red-600">{t('common.no')}</span>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Period */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="size-5" />
              {t('promotions.period')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t('promotions.startDate')}</span>
              <span>{formatDate(promotion.startDate, 'dd MMM yyyy HH:mm')}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t('promotions.endDate')}</span>
              <span>{formatDate(promotion.endDate, 'dd MMM yyyy HH:mm')}</span>
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t('promotions.createdAt')}</span>
              <span className="text-sm">{formatDate(promotion.createdAt)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t('promotions.updatedAt')}</span>
              <span className="text-sm">{formatDate(promotion.updatedAt)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Application Scope */}
        <Card className="md:col-span-2 lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="size-5" />
              {t('promotions.applicationScope')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t('promotions.applyToAllProducts')}</span>
              <div className="flex items-center gap-1">
                {promotion.applyToAllProducts ? (
                  <>
                    <CheckCircle className="size-4 text-green-500" />
                    <span className="text-green-600">{t('common.yes')}</span>
                  </>
                ) : (
                  <>
                    <XCircle className="size-4 text-muted-foreground" />
                    <span>{t('common.no')}</span>
                  </>
                )}
              </div>
            </div>
            {promotion.badgeColor && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t('promotions.badgeColor')}</span>
                <div className="flex items-center gap-2">
                  <div
                    className="size-6 rounded border"
                    style={{ backgroundColor: promotion.badgeColor }}
                  />
                  <code className="text-xs">{promotion.badgeColor}</code>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Associated Shops */}
        {Array.isArray(promotion.shops) && promotion.shops.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Store className="size-5" />
                {t('promotions.shops')} ({promotion.shops.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {promotion.shops.map((shop) => (
                  <Link key={shop.refId} to={`/shops/${shop.refId}`}>
                    <Badge variant="secondary" className="cursor-pointer hover:bg-secondary/80">
                      <Store className="size-3 mr-1" />
                      {shop.name}
                    </Badge>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Associated Products */}
        {Array.isArray(promotion.products) && promotion.products.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="size-5" />
                {t('promotions.products')} ({promotion.products.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {promotion.products.slice(0, 5).map((product) => (
                  <Link
                    key={product.refId}
                    to={`/products/${product.refId}`}
                    className="flex items-center gap-2 p-2 rounded hover:bg-muted transition-colors"
                  >
                    {Array.isArray(product.images) && product.images[0]?.url ? (
                      <img
                        src={getImageUrl(product.images[0].url) || product.images[0].url}
                        alt={product.name}
                        className="size-8 rounded object-cover"
                      />
                    ) : (
                      <div className="size-8 rounded bg-muted flex items-center justify-center">
                        <Package className="size-4 text-muted-foreground" />
                      </div>
                    )}
                    <span className="text-sm font-medium truncate">{product.name}</span>
                  </Link>
                ))}
                {promotion.products.length > 5 && (
                  <p className="text-xs text-muted-foreground text-center pt-2">
                    +{promotion.products.length - 5} {t('promotions.moreProducts')}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Associated Categories */}
        {Array.isArray(promotion.categories) && promotion.categories.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layers className="size-5" />
                {t('promotions.categories')} ({promotion.categories.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {promotion.categories.map((category) => (
                  <Badge key={category.id} variant="outline">
                    {t(`categories.${category.name}`, { defaultValue: category.name })}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Internal Notes */}
        {promotion.internalNotes && (
          <Card className="md:col-span-2 lg:col-span-3">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <StickyNote className="size-5" />
                {t('promotions.internalNotes')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground whitespace-pre-wrap">{promotion.internalNotes}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
